import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import './Persianas.css';

import PersianaIcon from './images/PersianaIcon.png';
import CortinasIcon from './images/CortinaIcon.png';

// Icons
import rollerIcon from './images/PRoller.png';
import romanIcon from './images/PRoma.png';
import panelIcon from './images/imagen3.png';
import zebraIcon from './images/imagen1.png';
import blackoutIcon from './images/CortinaIcon2.png';

// Product images (reemplaza por tus imágenes reales)
import rollerImg from './images/ROLLER.png';
import romanaImg from './images/ROMANA.png';
import panelImg from './images/PANELD.png';
import zebraImg from './images/ZEBRA.png';
import logosTelas from './images/LogosTelas.png'
import headerImg from './images/persianasHe.png'; 

import velo from './images/VeloSheerC.png';
import blackout from './images/BlackOutC.jpg';
import sunOut from './images/SunOutC.jpg';

const PersianasPremium = () => {
  const { t, i18n } = useTranslation();

  // estado UI
  const [mainTab, setMainTab] = useState('persianas'); // 'persianas' | 'cortinas'
  const [subTab, setSubTab] = useState('roller');      // según mainTab
  const [currentImage, setCurrentImage] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // título de la página
  useEffect(() => {
    document.title = t('meta.titleBlinds');
  }, [t, i18n.language]);

  // responsive
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // textos desde i18n (returnObjects)
  const text = t('blindsPage', { returnObjects: true });

  // mapas de iconos e imágenes (no van al JSON)
  const iconMap = {
    roller: rollerIcon,
    zebra: zebraIcon,
    romana: romanIcon,
    panel: panelIcon,
    blackout: blackoutIcon
  };
  const imagesMap = {
    persianas: {
      roller: [rollerImg, logosTelas],
      romana: [romanaImg, logosTelas],
      panel:  [panelImg, logosTelas],
      zebra:  [zebraImg, logosTelas]
    },
    cortinas: {
      blackout: [blackout,sunOut,velo,]
    }
  };

  // construir productData mezclando traducciones + iconos
  const productData = {
    persianas: {
      title: text.tabsTitles.persianas,
      subtitle: text.groupSubtitles.persianas,
      categories: text.categories.persianas.map(c => ({ ...c, icon: iconMap[c.id] })),
      content: text.content.persianas
    },
    cortinas: {
      title: text.tabsTitles.cortinas,
      subtitle: text.groupSubtitles.cortinas,
      categories: text.categories.cortinas.map(c => ({ ...c, icon: iconMap[c.id] })),
      content: text.content.cortinas
    }
  };

  // valores activos
  const { title, subtitle, categories, content } = productData[mainTab];
  const active = content[subTab];
  const images = imagesMap[mainTab][subTab];

  // navegación galería
  const prevImage = () => setCurrentImage(i => Math.max(i - 1, 0));
  const nextImage = () => setCurrentImage(i => Math.min(i + 1, images.length - 1));

  const handleWheel = (e) => {
    if (!isHovering) return;
    e.deltaY > 0 ? nextImage() : prevImage();
  };

  const MAIN_TABS = ['persianas', 'cortinas'];

  // JSON-LD
  const itemListJsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": title === text.tabsTitles.persianas ? "Tipos de persianas a medida" : "Tipos de cortinas a medida",
    "itemListElement": categories.map((c, idx) => ({
      "@type": "ListItem",
      "position": idx + 1,
      "name": c.label
    }))
  };
  const productJsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": active.caption,
    "description": active.description,
    "brand": { "@type": "Brand", "name": "RollerUp" },
    "category": mainTab === 'persianas' ? "Blinds" : "Curtains",
    "image": images,
    "offers": {
      "@type": "Offer",
      "priceCurrency": "CRC",
      "availability": "https://schema.org/InStock",
      "url": "https://rollerup.gruposantamaria.cr//productos"
    }
  };

  return (
    <div className="persianas-premium-container">

    {/* Header con imagen */}
    <div className="header-image-container">
      <img 
        src={headerImg} 
        alt="Persianas y cortinas a medida en Costa Rica" 
        className="header-image"
        loading="eager"
      />
      <div className="header-overlay">
        <h1 className="header-title">{text.ui.titleLine1} <span>{text.ui.titleLine2}</span></h1>
      </div>
    </div>


      {/* Fondo decorativo */}
      <div className="parallax-background" aria-hidden="true">
        <div className="bg-layer-1" />
        <div className="bg-layer-2" />
        <div className="bg-layer-3" />
      </div>

      {/* Distintivo colección */}
      <motion.div
        className="luxury-badge"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6, duration: 0.8 }}
        aria-label={text.aria.premiumCollection}
      >
        <div className="badge-inner">
          <span className="badge-icon">✧</span>
          <span>{text.ui.badge}</span>
        </div>
      </motion.div>

      <div className="content-wrapper">

        {/* Tabs principales */}
        <motion.div
          className="main-tabs-container"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          role="tablist"
          aria-label={text.aria.mainTablist}
        >
          {MAIN_TABS.map((key) => (
            <motion.button
              key={key}
              className={`main-tab ${mainTab === key ? 'active' : ''}`}
              onClick={() => {
                setMainTab(key);
                setSubTab(productData[key].categories[0].id);
                setCurrentImage(0);
              }}
              whileHover={{ y: -5 }}
              whileTap={{ scale: 0.97 }}
              role="tab"
              aria-selected={mainTab === key}
              aria-controls={`panel-${key}`}
              id={`tab-${key}`}
            >
              <div className="tab-icon-container">
                <img
                  src={key === 'persianas' ? PersianaIcon : CortinasIcon}
                  alt={`${productData[key].title} ${text.aria.icon}`}
                  className="tab-icon"
                />
              </div>
              <span>{productData[key].title}</span>
              {mainTab === key && (
                <motion.div
                  className="active-indicator"
                  layoutId="activeTab"
                  transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                />
              )}
            </motion.button>
          ))}
        </motion.div>

        {/* Subtabs */}
        <motion.div
          className="sub-tabs-container"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          role="tablist"
          aria-label={`${text.aria.subTablistPrefix} ${title.toLowerCase()}`}
        >
          {categories.map(category => (
            <motion.button
              key={category.id}
              className={`sub-tab ${subTab === category.id ? 'active' : ''}`}
              onClick={() => {
                setSubTab(category.id);
                setCurrentImage(0);
              }}
              whileHover={{ y: -3, backgroundColor: 'rgba(255, 255, 255, 0.15)' }}
              whileTap={{ scale: 0.95 }}
              role="tab"
              aria-selected={subTab === category.id}
              aria-controls={`panel-${mainTab}-${category.id}`}
              id={`tab-${mainTab}-${category.id}`}
            >
              <div className="sub-tab-icon-container">
                <img src={category.icon} alt={`${category.label} ${text.aria.icon}`} className="sub-tab-icon" />
              </div>
              <span>{category.label}</span>
              {subTab === category.id && (
                <motion.div
                  className="sub-active-indicator"
                  layoutId="activeSubTab"
                  transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                />
              )}
            </motion.button>
          ))}
        </motion.div>

        {/* Contenido principal */}
        <div
          className="product-content-container"
          id={`panel-${mainTab}`}
          role="tabpanel"
          aria-labelledby={`tab-${mainTab}`}
        >
          {/* Info */}
          <motion.div
            className="product-info-card"
            initial={{ x: -30, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            key={`${mainTab}-${subTab}-info`}
            id={`panel-${mainTab}-${subTab}`}
            role="tabpanel"
            aria-labelledby={`tab-${mainTab}-${subTab}`}
          >
            <motion.h2
              className="product-title"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              {active.caption}
            </motion.h2>

            <motion.p
              className="product-description"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              {active.description}
            </motion.p>

            <ul className="features-list">
              {active.bullets.map((bullet, index) => (
                <motion.li
                  key={index}
                  className="feature-item"
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.6 + index * 0.1 }}
                >
                  <div className="feature-icon">
                    <svg viewBox="0 0 24 24" width="20" height="20" color="#EA6226">
                      <path fill="currentColor" d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                    </svg>
                  </div>
                  <span>{bullet}</span>
                </motion.li>
              ))}
            </ul>

            <motion.div
              className="product-meta"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9 }}
            >
              <div className="meta-item">
                <span className="meta-label">{text.metaLabels.leadTime}</span>
                <span className="meta-value">{active.leadTime}</span>
              </div>
            </motion.div>

            <motion.div
              className="action-buttons"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1 }}
            >
              <motion.a
                href="https://wa.me/50660458068?text=¡Buenas!%20Vengo%20del%20sitio%20web%20y%20quisiera%20más%20información%20por%20favor.%20"
                className="primary-action"
                whileHover={{ y: -3, boxShadow: "0 10px 25px -5px rgba(234, 98, 38, 0.4)" }}
                whileTap={{ scale: 0.98 }}
                aria-label="WhatsApp"
              >
                <span>{text.buttons.quote}</span>
                <div className="arrow-icon">
                  <svg viewBox="0 0 24 24" width="20" height="20">
                    <path fill="currentColor" d="M5 13h11.17l-4.88 4.88c-.39.39-.39 1.03 0 1.42.39.39 1.02.39 1.41 0l6.59-6.59c.39-.39.39-1.02 0-1.41l-6.58-6.6c-.39-.39-1.02-.39-1.41 0-.39.39-.39 1.02 0 1.41L16.17 11H5c-.55 0-1 .45-1 1s.45 1 1 1z" />
                  </svg>
                </div>
              </motion.a>

              <motion.a
                href="https://flipbooks.gruposantamaria.cr/flipbook5"
                className="secondary-action"
                whileHover={{ y: -3 }}
                whileTap={{ scale: 0.98 }}
              >
                <span>{text.buttons.viewAll}</span>
              </motion.a>
            </motion.div>
          </motion.div>

          {/* Galería */}
          <div
            className="product-gallery"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
            onWheel={handleWheel}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={`${mainTab}-${subTab}-${currentImage}`}
                className="gallery-image-container"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              >
                <div className="image-wrapper">
                  <img
                    src={images[currentImage]}
                    alt={`${active.altBase} — ${active.caption} (imagen ${currentImage + 1})`}
                    className="product-image"
                    loading="lazy"
                  />
                </div>

                <div className={`gallery-controls ${isHovering || isMobile ? 'visible' : ''}`}>
                  <motion.button
                    onClick={prevImage}
                    disabled={currentImage === 0}
                    className="control-button prev"
                    whileHover={{ scale: 1.1, backgroundColor: 'rgba(255, 255, 255, 0.9)' }}
                    whileTap={{ scale: 0.9 }}
                    aria-label={text.aria.prevImage}
                  >
                    <svg viewBox="0 0 24 24" width="24" height="24">
                      <path fill="currentColor" d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z" />
                    </svg>
                  </motion.button>

                  <motion.button
                    onClick={nextImage}
                    disabled={currentImage === images.length - 1}
                    className="control-button next"
                    whileHover={{ scale: 1.1, backgroundColor: 'rgba(255, 255, 255, 0.9)' }}
                    whileTap={{ scale: 0.9 }}
                    aria-label={text.aria.nextImage}
                  >
                    <svg viewBox="0 0 24 24" width="24" height="24">
                      <path fill="currentColor" d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
                    </svg>
                  </motion.button>
                </div>

                <div className="image-counter" role="tablist" aria-label={text.aria.gallerySelect}>
                  {Array.from({ length: images.length }).map((_, i) => (
                    <button
                      key={i}
                      className={`counter-dot ${i === currentImage ? 'active' : ''}`}
                      onClick={() => setCurrentImage(i)}
                      role="tab"
                      aria-selected={i === currentImage}
                      aria-label={t('blindsPage.aria.image', { index: i + 1 })}
                    />
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* JSON-LD */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListJsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(productJsonLd) }} />
    </div>
  );
};

export default PersianasPremium;
